import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useRef, useState } from 'react';
import { TURNSTILE_SITE_KEY } from '../lib/config.js';

/**
 * Renders the Cloudflare Turnstile widget and exposes the current token to
 * the parent. If TURNSTILE_SITE_KEY is unset (only happens if a developer
 * blanks the constant for some offline session), the gate renders nothing
 * and the form submits unguarded.
 *
 * Managed mode auto-issues tokens for benign traffic; "challenge" UI only
 * appears for suspicious sessions. Tokens expire after ~5 minutes, so we
 * reset the widget on submit-success so a second submit gets a fresh token.
 */
export interface TurnstileGateHandle {
  reset(): void;
}

export default function TurnstileGate({
  onToken,
  onError,
}: {
  onToken: (token: string | null) => void;
  onError?: (msg: string) => void;
}) {
  const ref = useRef<TurnstileInstance | null>(null);
  const [errored, setErrored] = useState(false);

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <div className="mt-1">
      <Turnstile
        ref={ref}
        siteKey={TURNSTILE_SITE_KEY}
        options={{ theme: 'light', size: 'flexible' }}
        onSuccess={(token) => {
          setErrored(false);
          onToken(token);
        }}
        onExpire={() => onToken(null)}
        onError={() => {
          setErrored(true);
          onToken(null);
          onError?.('Bot check failed. Please refresh and try again.');
        }}
      />
      {errored && (
        <p className="mt-1 text-xs text-red-600">
          Couldn't load the bot check. Make sure adblockers aren't blocking
          challenges.cloudflare.com.
        </p>
      )}
    </div>
  );
}

export function turnstileEnabled(): boolean {
  return !!TURNSTILE_SITE_KEY;
}
